import { createEnum } from "@redotech/zod/zod-enum";

export enum BillingChargeColumnKey {
  RECORD_TYPE = "recordType",
  CREATED_AT = "createdAt",
  BILL_ID = "billId",
  CHARGE_TYPE = "chargeType",
  CHARGE_SUBTYPE = "chargeSubtype",
  AMOUNT = "amount",
  CURRENCY = "currency",
  TRANSACTION_STATUS = "transactionStatus",
  ORDER_ID = "orderId",
  RETURN_ID = "returnId",
  CAMPAIGN_ID = "campaignId",
  SHIPMENT_ID = "shipmentId",
  CARRIER_FEES = "carrierFees",
  INCLUDED_COUNT = "includedCount",
  OVERAGE_COUNT = "overageCount",
  ORDER_COUNT = "orderCount",
  SMS_COUNT = "smsCount",
  MMS_COUNT = "mmsCount",
  RECOVERED_REVENUE = "recoveredRevenue",
  SHOPIFY_CHARGE_ID = "shopifyChargeId",
  STRIPE_CHARGE_ID = "stripeChargeId",
  FAILURE_MESSAGE = "failureMessage",
}

export const BillingChargeColumnKeySchema = createEnum(
  Object.values(BillingChargeColumnKey) as [string, ...string[]],
);

export function billingChargeColumnSorterSlow(
  a: BillingChargeColumnKey,
  b: BillingChargeColumnKey,
): number {
  return Object.values(BillingChargeColumnKey).indexOf(a) <
    Object.values(BillingChargeColumnKey).indexOf(b)
    ? -1
    : 1;
}

export const billingChargeColumnDisplayNames: Record<
  BillingChargeColumnKey,
  string
> = {
  [BillingChargeColumnKey.RECORD_TYPE]: "Type",
  [BillingChargeColumnKey.CREATED_AT]: "Date",
  [BillingChargeColumnKey.BILL_ID]: "Invoice",
  [BillingChargeColumnKey.CHARGE_TYPE]: "Category",
  [BillingChargeColumnKey.CHARGE_SUBTYPE]: "Subcategory",
  [BillingChargeColumnKey.AMOUNT]: "Amount",
  [BillingChargeColumnKey.CURRENCY]: "Currency",
  [BillingChargeColumnKey.TRANSACTION_STATUS]: "Status",
  [BillingChargeColumnKey.ORDER_ID]: "Order ID",
  [BillingChargeColumnKey.RETURN_ID]: "Return ID",
  [BillingChargeColumnKey.CAMPAIGN_ID]: "Campaign ID",
  [BillingChargeColumnKey.SHIPMENT_ID]: "Shipment ID",
  [BillingChargeColumnKey.CARRIER_FEES]: "Carrier Fees",
  [BillingChargeColumnKey.INCLUDED_COUNT]: "Included Count",
  [BillingChargeColumnKey.OVERAGE_COUNT]: "Overage Count",
  [BillingChargeColumnKey.ORDER_COUNT]: "Order Count",
  [BillingChargeColumnKey.SMS_COUNT]: "SMS Count",
  [BillingChargeColumnKey.MMS_COUNT]: "MMS Count",
  [BillingChargeColumnKey.RECOVERED_REVENUE]: "Recovered Revenue",
  [BillingChargeColumnKey.SHOPIFY_CHARGE_ID]: "Shopify Charge ID",
  [BillingChargeColumnKey.STRIPE_CHARGE_ID]: "Stripe Charge ID",
  [BillingChargeColumnKey.FAILURE_MESSAGE]: "Failure Message",
};

export function getBillingChargeColumnDisplayName(
  columnKey: BillingChargeColumnKey,
): string {
  return billingChargeColumnDisplayNames[columnKey];
}

export const DEFAULT_BILLING_REPORT_COLUMNS: BillingChargeColumnKey[] = [
  BillingChargeColumnKey.CHARGE_TYPE,
  BillingChargeColumnKey.CHARGE_SUBTYPE,
  BillingChargeColumnKey.CREATED_AT,
  BillingChargeColumnKey.AMOUNT,
  BillingChargeColumnKey.CURRENCY,
  BillingChargeColumnKey.TRANSACTION_STATUS,
  BillingChargeColumnKey.RECORD_TYPE,
  BillingChargeColumnKey.BILL_ID,
  BillingChargeColumnKey.ORDER_ID,
];

export const CHARGE_COLUMNS: BillingChargeColumnKey[] = [
  BillingChargeColumnKey.RECORD_TYPE,
  BillingChargeColumnKey.CREATED_AT,
  BillingChargeColumnKey.BILL_ID,
  BillingChargeColumnKey.CHARGE_TYPE,
  BillingChargeColumnKey.CHARGE_SUBTYPE,
  BillingChargeColumnKey.AMOUNT,
  BillingChargeColumnKey.CURRENCY,
  BillingChargeColumnKey.ORDER_ID,
  BillingChargeColumnKey.RETURN_ID,
  BillingChargeColumnKey.CAMPAIGN_ID,
  BillingChargeColumnKey.SHIPMENT_ID,
  BillingChargeColumnKey.CARRIER_FEES,
  BillingChargeColumnKey.INCLUDED_COUNT,
  BillingChargeColumnKey.OVERAGE_COUNT,
  BillingChargeColumnKey.ORDER_COUNT,
  BillingChargeColumnKey.SMS_COUNT,
  BillingChargeColumnKey.MMS_COUNT,
  BillingChargeColumnKey.RECOVERED_REVENUE,
];

export const TRANSACTION_COLUMNS: BillingChargeColumnKey[] = [
  BillingChargeColumnKey.RECORD_TYPE,
  BillingChargeColumnKey.CREATED_AT,
  BillingChargeColumnKey.BILL_ID,
  BillingChargeColumnKey.AMOUNT,
  BillingChargeColumnKey.CURRENCY,
  BillingChargeColumnKey.TRANSACTION_STATUS,
  BillingChargeColumnKey.SHOPIFY_CHARGE_ID,
  BillingChargeColumnKey.STRIPE_CHARGE_ID,
  BillingChargeColumnKey.FAILURE_MESSAGE,
];

export const ALL_BILLING_COLUMNS: BillingChargeColumnKey[] = Object.values(
  BillingChargeColumnKey,
);
